import React, { useState, useEffect, useRef } from 'react';

const SRC = '/audio/navrachi.mp3';

// One player for the whole page, so the song can never play twice
// (React StrictMode mounts components twice in development).
let sharedAudio = null;
function getAudio() {
  if (!sharedAudio) {
    sharedAudio = new Audio(SRC);
    sharedAudio.loop = true;
    sharedAudio.volume = 0.5;
    sharedAudio.preload = 'auto';
  }
  return sharedAudio;
}

const AudioToggle = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [missing, setMissing] = useState(false); // shown briefly if the mp3 can't play
  const audioRef = useRef(null);
  const userPausedRef = useRef(false); // guest turned it off — never auto-start again

  useEffect(() => {
    const audio = getAudio();
    audioRef.current = audio;
    setIsPlaying(!audio.paused);
    let disposed = false;

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);

    // Browsers block sound until the guest interacts, so try right away and
    // otherwise start on the first tap / key press anywhere (e.g. opening the envelope).
    const events = ['pointerdown', 'keydown', 'touchstart'];
    const removeUnlock = () => events.forEach((ev) => document.removeEventListener(ev, unlock, true));
    function unlock(e) {
      if (disposed || userPausedRef.current) return removeUnlock();
      if (e.target?.closest?.('[data-audio-toggle]')) return; // the toggle handles its own tap
      audio.play().then(removeUnlock).catch(() => {});
    }
    if (audio.paused) {
      audio.play().catch(() => {
        if (!disposed) events.forEach((ev) => document.addEventListener(ev, unlock, true));
      });
    }

    return () => {
      disposed = true;
      removeUnlock();
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
    };
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      userPausedRef.current = true;
      audio.pause();
      return;
    }
    userPausedRef.current = false;
    audio.play().catch((e) => {
      console.warn('Audio playback failed (is public/audio/navrachi.mp3 present?)', e);
      setMissing(true);
      setTimeout(() => setMissing(false), 2500);
    });
  };

  return (
    <button
      data-audio-toggle
      onClick={togglePlay}
      aria-pressed={isPlaying}
      aria-label={isPlaying ? 'Pause the music' : 'Play the music'}
      title={missing ? 'Music coming soon' : isPlaying ? 'Music on' : 'Music off'}
      className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[700] w-12 h-12 sm:w-14 sm:h-14 rounded-full shadow-[0_8px_20px_rgba(58,10,24,0.45)] pointer-events-auto"
    >
      {/* Vinyl record: spins while the music plays, stops when paused */}
      <span
        className="relative block w-full h-full rounded-full"
        style={{
          background:
            'radial-gradient(circle, #B8174A 0 17%, #E8CC7E 17% 19%, transparent 19%),' +
            'repeating-radial-gradient(circle, #3A2418 0 1.5px, #4A2E1F 1.5px 3px)',
          animation: 'vinyl-spin 3.2s linear infinite',
          animationPlayState: isPlaying ? 'running' : 'paused',
        }}
      >
        <span className="absolute inset-0 rounded-full" style={{ background: 'conic-gradient(from 0deg, rgba(255,240,210,0.18), transparent 25%, rgba(255,240,210,0.12) 50%, transparent 75%)' }} />
        <span className="absolute left-1/2 top-1/2 w-1.5 h-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-ivory" />
      </span>
      {!isPlaying && (
        <span className="absolute -top-1 -left-1 w-5 h-5 rounded-full bg-ivory text-rani-deep text-[10px] flex items-center justify-center shadow">♪</span>
      )}
      <style>{`@keyframes vinyl-spin { to { transform: rotate(360deg); } }`}</style>
    </button>
  );
};

export default AudioToggle;
